import { action, makeObservable } from 'mobx'

import CalendarPlaceholderStore from '~/client/src/shared/components/CalendarDayView/components/CalendarPlaceholder.store'
import DeliveryDetailsStore from '~/client/src/shared/components/DeliveryDetails/DeliveryDetails.store'
import { IAppConfig } from '~/client/src/shared/Config'
import { NotificationsListStore } from '~/client/src/shared/lists/notifications/NotificationsList.store'
import Delivery from '~/client/src/shared/models/Delivery'
import type { EquipmentResource } from '~/client/src/shared/resources/domain/equipmentResource'
import {
  createEquipmentResourceType,
  type EquipmentResourceDomainFailure,
} from '~/client/src/shared/resources/domain/equipmentResourceTypeDefinition'
import type { LocationResource } from '~/client/src/shared/resources/domain/locationResource'
import {
  locationResourceType,
  type LocationResourceDomainFailure,
} from '~/client/src/shared/resources/domain/locationResourceTypeDefinition'
import type { MaterialResource } from '~/client/src/shared/resources/domain/materialResource'
import {
  materialResourceType,
  type MaterialResourceDomainFailure,
} from '~/client/src/shared/resources/domain/materialResourceTypeDefinition'
import type { SensorResource } from '~/client/src/shared/resources/domain/sensorResource'
import {
  sensorResourceType,
  type SensorResourceDomainFailure,
} from '~/client/src/shared/resources/domain/sensorResourceTypeDefinition'
import AuthenticationStore from '~/client/src/shared/stores/domain/Authentication.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import DeliveryStatusChangesStore from '~/client/src/shared/stores/domain/DeliveryStatusChanges.store'
import DeliveryUnitsStore from '~/client/src/shared/stores/domain/DeliveryUnits.store'
import DeliveryVehicleTypesStore from '~/client/src/shared/stores/domain/DeliveryVehicleTypes.store'
import GatesStore from '~/client/src/shared/stores/domain/Gates.store'
import MessagesStore from '~/client/src/shared/stores/domain/MessagesStore/Messages.store'
import NotificationsStore from '~/client/src/shared/stores/domain/Notifications.store'
import OffloadingEquipmentsStore from '~/client/src/shared/stores/domain/OffloadingEquipments.store'
import PhotosStore from '~/client/src/shared/stores/domain/Photos.store'
import ProjectResourcesStore from '~/client/src/shared/stores/domain/ProjectResources.store'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import ProjectsStore from '~/client/src/shared/stores/domain/Projects.store'
import ProjectTeamsStore from '~/client/src/shared/stores/domain/ProjectTeams.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import ThreadsStore from '~/client/src/shared/stores/domain/ThreadsStore/Threads.store'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import CommonStore from '~/client/src/shared/stores/ui/Common.store'
import SlackBarStore from '~/client/src/shared/stores/ui/SlackBar.store'
import WorkflowDrawingGuardStore from '~/client/src/shared/stores/ui/WorkflowDrawingGuard.store'
import Guard from '~/client/src/shared/utils/Guard'

import ApiAuthService from '../services/ApiAuthService'
import ChatService from '../services/ChatService/Chat.service'
import MapboxService from '../services/MapboxService'
import ProcoreService from '../services/ProcoreService'
import StruxHubAuthService from '../services/StruxHubAuthService'
import GraphExecutor from '../utils/GraphExecutor'
import AreasStore from './domain/Areas.store'
import BasemapsStore from './domain/Basemaps.store'
import BuildingsStore from './domain/Buildings.store'
import CalendarStore from './domain/Calendars.store'
import ClosuresStore from './domain/Closures.store'
import ConcreteDirectIntegrationStore from './domain/ConcreteDirectStores/ConcreteDirectIntegration.store'
import ConcreteDirectOrdersStore from './domain/ConcreteDirectStores/ConcreteDirectOrders.store'
import ConcreteDirectPayloadsStore from './domain/ConcreteDirectStores/ConcreteDirectPayloads.store'
import ConstraintsStore from './domain/Constraints.store'
import CustomDeliveryListFiltersStore from './domain/CustomDeliveryListFilters.store'
import CustomUploadLogFiltersStore from './domain/CustomUploadLogFilters.store'
import CustomWorkflowsListFiltersStore from './domain/CustomWorkflowsListFilter.store'
import DeliveryAssignmentsStore from './domain/DeliveryAssignments.store'
import DeliveryFollowingsStore from './domain/DeliveryFollowings.store'
import { FileUploadingStore } from './domain/FileUploading.store'
import GlobeViewsStore from './domain/GlobeViews.store'
import GraphExecutorStore from './domain/GraphExecutor.store'
import InteriorDoorsStore from './domain/InteriorDoors.store'
import InteriorPathsStore from './domain/InteriorPaths.store'
import JobsiteHomepageConfigurationStore from './domain/JobsiteHomepageConfiguration.store'
import LevelsStore from './domain/Levels.store'
import LocationAttributesStore from './domain/LocationAttributes.store'
import LocationIntegrationsStore from './domain/LocationIntegrations.store'
import LogisticsObjectsStore from './domain/LogisticsObjects.store'
import MarkupsStore from './domain/Markups.store'
import MaterialCategoryStore from './domain/MaterialCategories.store'
import MaterialConfigurationStore from './domain/MaterialConfiguration.store'
import MaterialsStore from './domain/Materials.store'
import MaturixProjectsStore from './domain/MaturixStores/MaturixProjects.store'
import OperationRulesStore from './domain/OperationRules.store'
import OtherLocationsStore from './domain/OtherLocations.store'
import PermitInspectionChangesStore from './domain/PermitInspectionChanges.store'
import PermitInspectionsStore from './domain/PermitInspections.store'
import PermitStatusChangesStore from './domain/PermitStatusChanges.store'
import ProcoreIntegrationStore from './domain/ProcoreStores/ProcoreIntegration.store'
import ProjectDefaultTeamsStore from './domain/ProjectDefaultTeams.store'
import ProjectLocationsStore from './domain/ProjectLocations.store'
import ProjectMembersStore from './domain/ProjectMembers.store'
import ProjectTradesStore from './domain/ProjectTrades.store'
import RecurringDeliveriesSettingsStore from './domain/RecurringDeliveriesSettings.store'
import RecurringWorkflowSettingsStore from './domain/RecurringWorkflowSettings.store'
import RemoteTilesetsStore from './domain/RemoteTilesets.store'
import RoutesStore from './domain/Routes.store'
import ScanHistoriesStore from './domain/ScanHistories.store'
import ScannersStore from './domain/Scanners.store'
import SharedDeliveriesStore from './domain/SharedDeliveries.store'
import SharedWorkflowStore from './domain/SharedWorkflow.store'
import SitemapItemsStore from './domain/SitemapItems.store'
import SitemapsStore from './domain/Sitemaps.store'
import StagingsStore from './domain/Stagings.store'
import StruxTagHistoryStore from './domain/StruxTagHistory.store'
import TenantsStore from './domain/Tenants.store'
import TilesetsStore from './domain/Tilesets.store'
import TilesetUploadProgressStore from './domain/TilesetUploadProgress.store'
import UploadLogConfigurationStore from './domain/UploadLogConfiguration.store'
import UserProjectsStore from './domain/UserProjects.store'
import VerticalObjectsStore from './domain/VerticalObjects.store'
import WeatherForecastsStore from './domain/WeatherForecasts.store'
import WhiteListItemsStore from './domain/WhiteListItems.store'
import WorkflowAssignmentsStore from './domain/WorkflowAssignments.store'
import WorkflowCategoriesStore from './domain/WorkflowCategories.store'
import WorkflowFollowingsStore from './domain/WorkflowFollowings.store'
import WorkflowsStore from './domain/Workflows.store'
import ZonesStore from './domain/Zones.store'
import PermitBallInCourtStore from './ui/PermitBallInCourt.store'
import ProjectDateStore from './ui/ProjectDate.store'

// TODO Split stores into domain and ui stores
// TODO Constructor inject all stores
class RootStore {
  public threadsStore: ThreadsStore
  public photosStore: PhotosStore
  public deliveryAssignmentsStore: DeliveryAssignmentsStore
  public workflowAssignmentsStore: WorkflowAssignmentsStore
  public sitePermitAssignmentsStore: WorkflowAssignmentsStore
  public workflowFollowingsStore: WorkflowFollowingsStore
  public deliveryFollowingsStore: DeliveryFollowingsStore
  public deliveriesStore: DeliveriesStore
  public common: CommonStore
  public auth: AuthenticationStore
  public slackBarStore: SlackBarStore
  public readonly workflowDrawingGuardStore = new WorkflowDrawingGuardStore()
  public messagesStore: MessagesStore
  public projectsStore: ProjectsStore
  public whiteListItemsStore: WhiteListItemsStore
  public maturixProjectsStore: MaturixProjectsStore
  public procoreIntegrationStore: ProcoreIntegrationStore
  public deliveryDetailsStore: DeliveryDetailsStore
  public constraintsStore: ConstraintsStore
  public levelsStore: LevelsStore
  public areasStore: AreasStore
  public logisticsObjectsStore: LogisticsObjectsStore
  public locationIntegrationsStore: LocationIntegrationsStore
  public stagingsStore: StagingsStore
  public interiorDoorsStore: InteriorDoorsStore
  public interiorPathsStore: InteriorPathsStore
  public verticalObjectsStore: VerticalObjectsStore
  public zonesStore: ZonesStore
  public offloadingEquipmentsStore: OffloadingEquipmentsStore
  public gatesStore: GatesStore
  public projectLocationsStore: ProjectLocationsStore
  public otherLocationsStore: OtherLocationsStore
  public buildingsStore: BuildingsStore
  public routesStore: RoutesStore
  public locationAttributesStore: LocationAttributesStore
  public deliveryVehicleTypesStore: DeliveryVehicleTypesStore
  public deliveryUnitsStore: DeliveryUnitsStore
  public customDeliveryListFiltersStore: CustomDeliveryListFiltersStore
  public customUploadLogFiltersStore: CustomUploadLogFiltersStore
  public uploadLogConfigurationStore: UploadLogConfigurationStore
  public customWorkflowsListFiltersStore: CustomWorkflowsListFiltersStore
  public calendarPlaceholderStore: CalendarPlaceholderStore
  public deliveryStatusChangesStore: DeliveryStatusChangesStore
  public permitStatusChangesStore: PermitStatusChangesStore
  public permitInspectionsStore: PermitInspectionsStore
  public permitInspectionChangesStore: PermitInspectionChangesStore
  public basemapsStore: BasemapsStore
  public sitemapsStore: SitemapsStore
  public tilesetsStore: TilesetsStore
  public tilesetUploadProgressStore: TilesetUploadProgressStore
  public sitemapItemsStore: SitemapItemsStore
  public markupsStore: MarkupsStore
  public projectDateStore: ProjectDateStore
  public materialCategoryStore: MaterialCategoryStore
  public equipmentResourcesStore: ProjectResourcesStore<
    EquipmentResource,
    EquipmentResourceDomainFailure
  >
  public materialResourcesStore: ProjectResourcesStore<
    MaterialResource,
    MaterialResourceDomainFailure
  >
  public materialsStore: MaterialsStore
  public sensorResourcesStore: ProjectResourcesStore<
    SensorResource,
    SensorResourceDomainFailure
  >
  public locationResourcesStore: ProjectResourcesStore<
    LocationResource,
    LocationResourceDomainFailure
  >
  public struxTagHistoryStore: StruxTagHistoryStore
  public calendarStore: CalendarStore
  public companiesStore: CompaniesStore
  public workflowTypesStore: WorkflowTypesStore
  public permitTypesStore: WorkflowTypesStore
  public projectRolesStore: ProjectRolesStore
  public projectTeamsStore: ProjectTeamsStore
  public projectTradesStore: ProjectTradesStore
  public projectDefaultTeamsStore: ProjectDefaultTeamsStore
  public workflowsStore: WorkflowsStore
  public sitePermitsStore: WorkflowsStore
  public tagsStore: TagsStore
  public closuresStore: ClosuresStore
  public operationRulesStore: OperationRulesStore
  public userProjectsStore: UserProjectsStore
  public projectMembersStore: ProjectMembersStore
  public graphExecutorStore: GraphExecutorStore
  public weatherForecastsStore: WeatherForecastsStore
  public fileUploadingStore: FileUploadingStore
  public recurringDeliveriesSettingsStore: RecurringDeliveriesSettingsStore
  public recurringWorkflowSettingsStore: RecurringWorkflowSettingsStore
  public concreteDirectIntegrationStore: ConcreteDirectIntegrationStore
  public concreteDirectOrdersStore: ConcreteDirectOrdersStore
  public concreteDirectPayloadsStore: ConcreteDirectPayloadsStore
  public scannersStore: ScannersStore
  public scanHistoriesStore: ScanHistoriesStore
  public globeViewsStore: GlobeViewsStore
  public jobsiteHomepageConfigurationStore: JobsiteHomepageConfigurationStore
  public materialConfigurationStore: MaterialConfigurationStore
  public tenantsStore: TenantsStore
  public workflowCategoriesStore: WorkflowCategoriesStore
  public formCategoriesStore: WorkflowCategoriesStore
  public sharedDeliveriesStore: SharedDeliveriesStore
  public sharedWorkflowsStore: SharedWorkflowStore
  public sharedSitePermitStore: SharedWorkflowStore
  public remoteTilesetsStore: RemoteTilesetsStore
  public permitBicStore: PermitBallInCourtStore
  public notificationsStore: NotificationsStore
  public notificationsListStore: NotificationsListStore

  public readonly chatService: ChatService = null
  public readonly mapboxService: MapboxService = null
  public readonly struxHubAuthService: StruxHubAuthService = null

  public readonly isMobileEnv: boolean

  public constructor(
    public state: InitialState,
    public configuration: IAppConfig,
    public eventsStore: EventsStore,
    CommonStoreModel: new (state: InitialState, root: RootStore) => CommonStore,
    protected apiAuthService: ApiAuthService,
    struxHubAuthService: StruxHubAuthService,
    public procoreService: ProcoreService,
    graphExecutor: GraphExecutor,
    isTestEnv: boolean,
    isMobileEnv?: boolean,
  ) {
    makeObservable(this)
    Guard.againstUndefined(state, 'initialState')
    Guard.againstUndefined(apiAuthService, 'apiAuthService')
    Guard.requireAll({ configuration })

    this.isMobileEnv = isMobileEnv || false

    this.struxHubAuthService = struxHubAuthService

    this.chatService = new ChatService(
      this.struxHubAuthService.getValidAccessToken,
    )
    this.mapboxService = new MapboxService(
      this.struxHubAuthService.getValidAccessToken,
    )

    this.graphExecutorStore = new GraphExecutorStore(
      graphExecutor,
      eventsStore,
      isTestEnv,
    )
    this.equipmentResourcesStore = new ProjectResourcesStore(
      this.graphExecutorStore,
      createEquipmentResourceType(state),
    )
    this.materialResourcesStore = new ProjectResourcesStore(
      this.graphExecutorStore,
      materialResourceType,
    )
    this.sensorResourcesStore = new ProjectResourcesStore(
      this.graphExecutorStore,
      sensorResourceType,
    )
    this.locationResourcesStore = new ProjectResourcesStore(
      this.graphExecutorStore,
      locationResourceType,
    )
    this.struxTagHistoryStore = new StruxTagHistoryStore(
      this.graphExecutorStore,
    )
    this.fileUploadingStore = new FileUploadingStore(
      this.graphExecutorStore,
      this.mapboxService,
    )
    this.projectDateStore = new ProjectDateStore(state)

    this.operationRulesStore = new OperationRulesStore(this.eventsStore)

    this.userProjectsStore = new UserProjectsStore(this.eventsStore)

    this.sharedDeliveriesStore = new SharedDeliveriesStore()
    this.sharedWorkflowsStore = new SharedWorkflowStore()
    this.sharedSitePermitStore = this.sharedWorkflowsStore
    this.projectMembersStore = new ProjectMembersStore(
      this.eventsStore,
      this.userProjectsStore,
      this.graphExecutorStore,
    )

    this.buildingsStore = new BuildingsStore(
      this.eventsStore,
      this.graphExecutorStore,
      this.locationResourcesStore,
    )

    this.levelsStore = new LevelsStore(
      this.eventsStore,
      this.graphExecutorStore,
      this.buildingsStore,
    )
    this.areasStore = new AreasStore(this.eventsStore, this.graphExecutorStore)

    this.logisticsObjectsStore = new LogisticsObjectsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )
    this.locationIntegrationsStore = new LocationIntegrationsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )
    this.stagingsStore = new StagingsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )
    this.interiorDoorsStore = new InteriorDoorsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )
    this.interiorPathsStore = new InteriorPathsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )
    this.verticalObjectsStore = new VerticalObjectsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.zonesStore = new ZonesStore(this.eventsStore, this.graphExecutorStore)

    this.offloadingEquipmentsStore = new OffloadingEquipmentsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.gatesStore = new GatesStore(this.eventsStore, this.graphExecutorStore)

    this.projectLocationsStore = new ProjectLocationsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.routesStore = new RoutesStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.otherLocationsStore = new OtherLocationsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.locationAttributesStore = new LocationAttributesStore(
      this.gatesStore,
      this.zonesStore,
      this.buildingsStore,
      this.routesStore,
      this.offloadingEquipmentsStore,
      this.stagingsStore,
      this.interiorDoorsStore,
      this.interiorPathsStore,
      this.levelsStore,
      this.areasStore,
      this.logisticsObjectsStore,
      this.locationIntegrationsStore,
      this.verticalObjectsStore,
      this.projectLocationsStore,
      this.otherLocationsStore,
    )

    this.calendarPlaceholderStore = new CalendarPlaceholderStore(
      this.projectDateStore,
      this.companiesStore,
    )

    this.companiesStore = new CompaniesStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.whiteListItemsStore = new WhiteListItemsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.projectsStore = new ProjectsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.maturixProjectsStore = new MaturixProjectsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.procoreIntegrationStore = new ProcoreIntegrationStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.deliveryStatusChangesStore = new DeliveryStatusChangesStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.permitStatusChangesStore = new PermitStatusChangesStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.permitInspectionsStore = new PermitInspectionsStore(this.eventsStore)

    this.permitInspectionChangesStore = new PermitInspectionChangesStore(
      this.eventsStore,
    )

    this.common = new CommonStoreModel(this.state, this)

    this.projectTeamsStore = new ProjectTeamsStore(this.eventsStore)

    this.projectTradesStore = new ProjectTradesStore(this.eventsStore)

    this.projectDefaultTeamsStore = new ProjectDefaultTeamsStore(
      this.eventsStore,
    )

    this.projectRolesStore = new ProjectRolesStore(this.eventsStore)

    this.workflowCategoriesStore = new WorkflowCategoriesStore(
      this.eventsStore,
      this.graphExecutorStore,
    )
    this.formCategoriesStore = this.workflowCategoriesStore

    // Built before its consumers so the one shared instance is injected, not re-newed.
    this.markupsStore = new MarkupsStore(this.eventsStore)
    this.workflowsStore = new WorkflowsStore(
      this.eventsStore,
      this.graphExecutorStore,
      this.markupsStore,
    )
    this.sitePermitsStore = this.workflowsStore

    this.workflowTypesStore = new WorkflowTypesStore(
      this.eventsStore,
      this.workflowCategoriesStore,
    )
    this.permitTypesStore = this.workflowTypesStore

    this.tenantsStore = new TenantsStore(this.state, this.struxHubAuthService)

    this.auth = new AuthenticationStore(
      this.common,
      this.struxHubAuthService,
      this.apiAuthService,
      this.eventsStore,
      this.state,
      this.tenantsStore,
    )

    this.messagesStore = new MessagesStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.threadsStore = new ThreadsStore(this.eventsStore)

    this.photosStore = new PhotosStore(
      this.eventsStore,
      this.fileUploadingStore,
      this.messagesStore,
      this.projectDateStore,
    )

    this.deliveryUnitsStore = new DeliveryUnitsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.deliveriesStore = new DeliveriesStore(
      this.eventsStore,
      this.deliveryUnitsStore,
    )

    // A delivery's location snapshots go stale when a location changes kind, so the
    // delivery form's slots read the live location instead (STX1-597).
    Delivery.setLocationKindResolver(
      this.locationAttributesStore.getLocationRootKindById,
    )

    this.notificationsStore = new NotificationsStore(
      this.eventsStore,
      this.deliveriesStore,
      this.deliveryStatusChangesStore,
      this.companiesStore,
      this.graphExecutorStore,
      this.projectDateStore,
      this.permitTypesStore,
    )

    this.deliveryVehicleTypesStore = new DeliveryVehicleTypesStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.customWorkflowsListFiltersStore = new CustomWorkflowsListFiltersStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.customDeliveryListFiltersStore = new CustomDeliveryListFiltersStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.customUploadLogFiltersStore = new CustomUploadLogFiltersStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.uploadLogConfigurationStore = new UploadLogConfigurationStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.permitBicStore = new PermitBallInCourtStore(
      this.eventsStore,
      this.operationRulesStore,
      this.projectMembersStore,
      this.workflowTypesStore,
      this.companiesStore,
    )
    this.notificationsStore.permitBicStore = this.permitBicStore
    this.notificationsListStore = new NotificationsListStore({
      notificationsStore: this.notificationsStore,
      deliveriesStore: this.deliveriesStore,
      companiesStore: this.companiesStore,
      permitTypesStore: this.permitTypesStore,
      projectMembersStore: this.projectMembersStore,
      locationAttributesStore: this.locationAttributesStore,
      permitBicStore: this.permitBicStore,
      getCurrentUserId: () => this.state.user?.id,
    })

    this.remoteTilesetsStore = new RemoteTilesetsStore(this.eventsStore)

    this.workflowFollowingsStore = new WorkflowFollowingsStore(this.eventsStore)

    this.deliveryFollowingsStore = new DeliveryFollowingsStore(this.eventsStore)

    this.scannersStore = new ScannersStore(this.eventsStore)
    this.scanHistoriesStore = new ScanHistoriesStore(
      this.eventsStore,
      this.scannersStore,
    )

    this.closuresStore = new ClosuresStore(
      this.state,
      this.graphExecutorStore,
      this.locationAttributesStore,
      this.projectDateStore,
    )

    this.calendarStore = new CalendarStore(this.eventsStore)

    this.basemapsStore = new BasemapsStore(this.eventsStore)

    this.sitemapsStore = new SitemapsStore(this.eventsStore)

    this.tilesetUploadProgressStore = new TilesetUploadProgressStore()

    this.tilesetsStore = new TilesetsStore(
      this.eventsStore,
      this.fileUploadingStore,
      this.mapboxService,
      this.tilesetUploadProgressStore,
    )

    this.sitemapItemsStore = new SitemapItemsStore(this.eventsStore)

    this.materialCategoryStore = new MaterialCategoryStore(this.eventsStore)
    this.materialsStore = new MaterialsStore(
      this.eventsStore,
      this.graphExecutorStore,
      this.deliveriesStore,
      this.materialCategoryStore,
      this.locationAttributesStore,
      this.sitePermitsStore,
      this.permitTypesStore,
    )

    this.tagsStore = new TagsStore(
      this.locationAttributesStore,
      this.companiesStore,
      this.projectTeamsStore,
      this.projectDefaultTeamsStore,
      this.projectRolesStore,
      this.projectTradesStore,
      this.userProjectsStore,
      this.projectMembersStore,
      this.permitTypesStore,
      this.formCategoriesStore,
    )

    this.deliveryAssignmentsStore = new DeliveryAssignmentsStore(
      this.eventsStore,
      this.tagsStore,
    )

    this.workflowAssignmentsStore = new WorkflowAssignmentsStore(
      this.eventsStore,
      this.tagsStore,
    )
    this.sitePermitAssignmentsStore = this.workflowAssignmentsStore

    this.constraintsStore = new ConstraintsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.globeViewsStore = new GlobeViewsStore(
      this.eventsStore,
      this.locationAttributesStore,
      this.sitemapItemsStore,
    )

    this.recurringDeliveriesSettingsStore =
      new RecurringDeliveriesSettingsStore(this.eventsStore)

    this.recurringWorkflowSettingsStore = new RecurringWorkflowSettingsStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.concreteDirectIntegrationStore = new ConcreteDirectIntegrationStore(
      this.eventsStore,
      this.graphExecutorStore,
    )

    this.concreteDirectOrdersStore = new ConcreteDirectOrdersStore(
      this.eventsStore,
      this.graphExecutorStore,
      this.concreteDirectIntegrationStore,
    )

    this.concreteDirectPayloadsStore = new ConcreteDirectPayloadsStore(
      this.eventsStore,
      this.graphExecutorStore,
      this.concreteDirectIntegrationStore,
    )

    this.jobsiteHomepageConfigurationStore =
      new JobsiteHomepageConfigurationStore(
        this.eventsStore.appState,
        this.graphExecutorStore,
      )

    this.materialConfigurationStore = new MaterialConfigurationStore(
      this.eventsStore.appState,
      this.graphExecutorStore,
    )

    this.deliveryDetailsStore = new DeliveryDetailsStore(
      this.eventsStore,
      this.sitemapsStore,
      this.deliveryVehicleTypesStore,
      this.deliveryUnitsStore,
      this.deliveryStatusChangesStore,
      this.calendarPlaceholderStore,
      this.projectDateStore,
      this.materialCategoryStore,
      this.materialsStore,
      this.companiesStore,
      this.deliveriesStore,
      this.threadsStore,
      this.messagesStore,
      this.photosStore,
      this.closuresStore,
      this.tagsStore,
      this.sitemapItemsStore,
      this.fileUploadingStore,
      this.userProjectsStore,
      this.projectMembersStore,
      this.graphExecutorStore,
      this.deliveryFollowingsStore,
      this.constraintsStore,
      this.locationAttributesStore,
      this.common,
      this.deliveryAssignmentsStore,
      this.recurringDeliveriesSettingsStore,
      this.concreteDirectOrdersStore,
      this.concreteDirectPayloadsStore,
      this.globeViewsStore,
      this.sharedDeliveriesStore,
      this.projectsStore,
    )

    this.slackBarStore = new SlackBarStore(
      this.eventsStore,
      this.threadsStore,
      this.messagesStore,
      this.photosStore,
      this.deliveriesStore,
      this.deliveryStatusChangesStore,
      this.fileUploadingStore,
      this.userProjectsStore,
      this.tagsStore,
      this.deliveryFollowingsStore,
    )

    this.weatherForecastsStore = new WeatherForecastsStore(
      this.eventsStore,
      this.projectDateStore,
    )
  }

  @action
  public clearOutTrackingData() {
    this.calendarStore.clearList()
  }
}

export default RootStore
